import React, { useState, useEffect } from 'react';
import { SecurityEvent } from '../types';
import { eventsAPI } from '../services/api';
import '../styles/EventsPage.css';

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [namespaceFilter, setNamespaceFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [sortBy, setSortBy] = useState<'timestamp' | 'severity'>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await eventsAPI.getAll(100, 0);
        setEvents(data.data);
        setError(null);
      } catch (err) {
        setError('Failed to load events');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = events.filter((event) => {
    const matchesSeverity = severityFilter === 'all' || event.severity === severityFilter;
    const matchesNamespace = namespaceFilter === 'all' || event.namespace === namespaceFilter;
    const matchesSearch =
      searchTerm === '' ||
      event.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.pod.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSeverity && matchesNamespace && matchesSearch;
  });

  // Sorting
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    let compareValue = 0;

    if (sortBy === 'timestamp') {
      compareValue = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    } else if (sortBy === 'severity') {
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      compareValue = severityOrder[b.severity as keyof typeof severityOrder] - 
                     severityOrder[a.severity as keyof typeof severityOrder];
    }

    return sortOrder === 'asc' ? compareValue : -compareValue;
  });

  const handleSort = (column: 'timestamp' | 'severity') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Pagination
  const totalPages = Math.ceil(sortedEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEvents = sortedEvents.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const uniqueNamespaces = Array.from(new Set(events.map((e) => e.namespace)));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="events-page">
        <header className="page-header">
          <h1>Security Events</h1>
          <p>Monitor all security events in your Kubernetes cluster</p>
        </header>
        <div className="loading">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="events-page">
      <header className="page-header">
        <h1>Security Events</h1>
        <p>Total: {events.length} events | Filtered: {filteredEvents.length}</p>
      </header>

      <div className="page-container">
        {error && <div className="error">{error}</div>}

        <section className="filters-section">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by pod name or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label>Severity</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value="all">All Severities</option>
              <option value="critical">🔴 Critical</option>
              <option value="high">🟠 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Namespace</label>
            <select
              value={namespaceFilter}
              onChange={(e) => setNamespaceFilter(e.target.value)}
            >
              <option value="all">All Namespaces</option>
              {uniqueNamespaces.map((ns) => (
                <option key={ns} value={ns}>
                  {ns}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="events-table-section">
          <table className="events-table">
            <thead>
              <tr>
                <th
                  className={`sortable ${sortBy === 'timestamp' ? 'active' : ''}`}
                  onClick={() => handleSort('timestamp')}
                >
                  Timestamp {sortBy === 'timestamp' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className={`sortable ${sortBy === 'severity' ? 'active' : ''}`}
                  onClick={() => handleSort('severity')}
                >
                  Severity {sortBy === 'severity' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Type</th>
                <th>Pod</th>
                <th>Namespace</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEvents.map((event) => (
                <tr key={event.id}>
                  <td className="timestamp">{formatDate(event.timestamp)}</td>
                  <td>
                    <span className={`badge severity-${event.severity}`}>
                      {event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}
                    </span>
                  </td>
                  <td>{event.type}</td>
                  <td>{event.pod}</td>
                  <td>{event.namespace}</td>
                  <td className="message-cell">{event.message}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {paginatedEvents.length === 0 && (
            <div className="no-data">No events found matching your filters</div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              <div className="page-info">
                Page {currentPage} of {totalPages} (Showing {paginatedEvents.length} of {sortedEvents.length} events)
              </div>
              
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default EventsPage;
